import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { originForApp } from "@utils/appOrigin";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { Button } from "@shadcn/ui/button";
import { KeyboardAwareDrawer, KeyboardAwareDrawerContent } from "@components/KeyboardAwareDrawer";
import { Checkbox } from "@shadcn/ui/checkbox";
import { DrawerClose, DrawerDescription, DrawerHeader, DrawerTitle } from "@shadcn/ui/drawer";
import { Input } from "@shadcn/ui/input";
import { Label } from "@shadcn/ui/label";
import { useRouter } from "@tanstack/react-router";
import { X } from "lucide-react";
import { useState } from "react";
import { graphql } from "relay-runtime";
import { toast } from "sonner";
import type { ShareAccessDrawerInviteMutation } from "./__generated__/ShareAccessDrawerInviteMutation.graphql";

export type LockPermission = "UNLOCK" | "END_RENTAL";

// A basic RFC-ish email check; the backend is the source of truth, this only
// keeps the user from submitting an obviously invalid address.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Share access with a recipient by email. The recipient does NOT need a bace
// account: the backend generates a secure access token and emails them an
// invite link with the instructions to open the compartment.
const inviteMutation = graphql`
	mutation ShareAccessDrawerInviteMutation($input: UtilizationGrantInviteAnonymousInput!) {
		lockUtilizationGrantInviteAnonymous(input: $input) {
			id
			expiresAt
		}
	}
`;

type ShareAccessDrawerProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onShared?: () => void;
	reservationId: string;
};

export function ShareAccessDrawer(props: ShareAccessDrawerProps) {
	const i18n = useI18nContext().LL["new-ui"];
	return (
		<KeyboardAwareDrawer open={props.open} onOpenChange={props.onOpenChange}>
			<KeyboardAwareDrawerContent>
				<DrawerHeader className="flex-row items-start justify-between gap-4 text-left">
					<div className="flex flex-col gap-1">
						<DrawerTitle className="text-2xl font-breit font-medium">
							<T>{i18n.LOCK_SHARE_TITLE()}</T>
						</DrawerTitle>
						<DrawerDescription>
							<T>{i18n.LOCK_SHARE_DESC()}</T>
						</DrawerDescription>
					</div>
					<DrawerClose asChild>
						<Button variant="ghost" size="icon" className="shrink-0">
							<X className="size-5" />
						</Button>
					</DrawerClose>
				</DrawerHeader>

				{props.open ? (
					<ShareAccessBody
						reservationId={props.reservationId}
						onShared={() => {
							props.onOpenChange(false);
							props.onShared?.();
						}}
					/>
				) : null}
			</KeyboardAwareDrawerContent>
		</KeyboardAwareDrawer>
	);
}

function ShareAccessBody(props: { reservationId: string; onShared: () => void }) {
	const i18n = useI18nContext().LL["new-ui"];
	const router = useRouter();

	const [invite, isInviting] = useAsyncMutation<ShareAccessDrawerInviteMutation>(inviteMutation);

	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [permissions, setPermissions] = useState<LockPermission[]>(["UNLOCK"]);
	const [showErrors, setShowErrors] = useState(false);

	const trimmedName = name.trim();
	const trimmedEmail = email.trim();
	const nameError = trimmedName.length === 0 ? i18n.LOCK_SHARE_NAME_REQUIRED() : undefined;
	const emailError =
		trimmedEmail.length === 0
			? i18n.LOCK_SHARE_EMAIL_REQUIRED()
			: !EMAIL_REGEX.test(trimmedEmail)
				? i18n.LOCK_SHARE_EMAIL_INVALID()
				: undefined;
	const permissionsError = permissions.length === 0 ? i18n.LOCK_SHARE_AUTHORIZATION_REQUIRED() : undefined;

	const togglePermission = (permission: LockPermission) => {
		setPermissions((current) =>
			current.includes(permission) ? current.filter((item) => item !== permission) : [...current, permission],
		);
	};

	const handleShare = async () => {
		if (nameError || emailError || permissionsError) {
			setShowErrors(true);
			return;
		}
		try {
			await invite({
				variables: {
					input: {
						reservationId: props.reservationId,
						email: trimmedEmail,
						permissions,
						returnBaseUrl: originForApp("app"),
					},
				},
			});
			toast.success(i18n.LOCK_SHARE_SUCCESS());
			await router.invalidate();
			props.onShared();
		} catch (error) {
			console.error(error);
			toast.error(i18n.LOCK_SHARE_FAILED());
		}
	};

	return (
		<div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto border-t px-4 pb-4 pt-4">
			{/* Receiver full name */}
			<div className="flex flex-col gap-2">
				<Label htmlFor="share-name" className="text-sm font-breit font-medium">
					<T>{i18n.LOCK_SHARE_NAME_LABEL()}</T>
				</Label>
				<Input
					id="share-name"
					value={name}
					placeholder={i18n.LOCK_SHARE_NAME_PLACEHOLDER()}
					onChange={(e) => setName(e.target.value)}
					aria-invalid={showErrors && nameError ? true : undefined}
					aria-describedby={showErrors && nameError ? "share-name-error" : undefined}
				/>
				{showErrors && nameError ? (
					<p id="share-name-error" className="text-sm text-destructive">
						<T>{nameError}</T>
					</p>
				) : null}
			</div>

			{/* Receiver email */}
			<div className="flex flex-col gap-2">
				<Label htmlFor="share-email" className="text-sm font-breit font-medium">
					<T>{i18n.LOCK_SHARE_EMAIL_LABEL()}</T>
				</Label>
				<Input
					id="share-email"
					type="email"
					inputMode="email"
					autoComplete="email"
					value={email}
					placeholder={i18n.LOCK_SHARE_EMAIL_PLACEHOLDER()}
					onChange={(e) => setEmail(e.target.value)}
					aria-invalid={showErrors && emailError ? true : undefined}
					aria-describedby={showErrors && emailError ? "share-email-error" : undefined}
				/>
				{showErrors && emailError ? (
					<p id="share-email-error" className="text-sm text-destructive">
						<T>{emailError}</T>
					</p>
				) : null}
			</div>

			{/* Authorization */}
			<div className="flex flex-col gap-2">
				<span className="text-sm font-breit font-medium">
					<T>{i18n.LOCK_SHARE_AUTHORIZATION_LABEL()}</T>
				</span>
				<div className="flex flex-col gap-3">
					<AuthorizationRow
						id="share-perm-unlock"
						checked={permissions.includes("UNLOCK")}
						onChange={() => togglePermission("UNLOCK")}
						label={i18n.LOCK_SHARE_PERMISSION_UNLOCK()}
						description={i18n.LOCK_SHARE_PERMISSION_UNLOCK_DESC()}
					/>
					<AuthorizationRow
						id="share-perm-end-rental"
						checked={permissions.includes("END_RENTAL")}
						onChange={() => togglePermission("END_RENTAL")}
						label={i18n.LOCK_SHARE_PERMISSION_END_RENTAL()}
						description={i18n.LOCK_SHARE_PERMISSION_END_RENTAL_DESC()}
					/>
				</div>
				{showErrors && permissionsError ? (
					<p className="text-sm text-destructive">
						<T>{permissionsError}</T>
					</p>
				) : null}
			</div>

			<div className="flex flex-col gap-2 pt-2">
				<Button className="w-full shadow-xs" disabled={isInviting} onClick={() => void handleShare()}>
					<T>{i18n.LOCK_SHARE_SUBMIT()}</T>
				</Button>
				<DrawerClose asChild>
					<Button variant="outline" className="w-full" disabled={isInviting}>
						<T>{i18n.LOCK_CONTENT_CANCEL()}</T>
					</Button>
				</DrawerClose>
			</div>
		</div>
	);
}

/** Two uppercase initials from a name or email, for the avatar fallback. */
export function getInitials(name: string | null | undefined, email: string | null | undefined): string {
	const source = (name ?? email ?? "").trim();
	if (!source) return "?";
	const parts = source.split(/\s+/).filter(Boolean);
	if (parts.length >= 2) return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
	return source.slice(0, 2).toUpperCase();
}

function AuthorizationRow(props: {
	id: string;
	checked: boolean;
	onChange: () => void;
	label: string;
	description: string;
}) {
	return (
		<label htmlFor={props.id} className="flex cursor-pointer items-start gap-3">
			<Checkbox id={props.id} checked={props.checked} onCheckedChange={props.onChange} className="mt-0.5 size-5 shrink-0" />
			<span className="flex flex-col gap-0.5">
				<span className="text-sm font-breit">{props.label}</span>
				<span className="text-sm text-muted-foreground">{props.description}</span>
			</span>
		</label>
	);
}
